/**
 * PURPOSE: Contexto de compañía efectiva de la pestaña y cabecera X-Hayllis-Company (FTR-0012).
 * RESPONSIBILITIES: Guardar la compañía validada de esta pestaña (estado de módulo: una pestaña =
 *   un runtime JS), y añadir la cabecera solo a requests dirigidos a la API de Winscore — nunca a
 *   hosts externos, donde una cabecera propia dispararía un preflight CORS fallido.
 *   Sin dependencias de axios para poder probarse de forma aislada.
 * COLLABORATORS: src/services/httpService.js, src/index.js (instancia axios por defecto),
 *   src/hooks/useEffectiveCompany.js.
 * ROUTE: winscore/src/services/effectiveCompanyContext.js
 */
export const COMPANY_CONTEXT_HEADER = "X-Hayllis-Company";

const MAX_MYSQL_INT = 2147483647;
const ABSOLUTE_URL = /^[a-z][a-z\d+\-.]*:\/\//i;

/** Mismo parseo estricto que el backend: solo dígitos, entero positivo. */
export const parseCompanyId = (value) => {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 && value <= MAX_MYSQL_INT ? value : null;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!/^[1-9]\d{0,9}$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return parsed <= MAX_MYSQL_INT ? parsed : null;
};

let activeCompanyId = null;

export const setActiveCompanyContext = (companyId) => {
  activeCompanyId = parseCompanyId(companyId);
  return activeCompanyId;
};

/** Limpia el contexto; con `expectedCompanyId` solo si sigue siendo el activo. */
export const clearActiveCompanyContext = (expectedCompanyId) => {
  if (expectedCompanyId === undefined || parseCompanyId(expectedCompanyId) === activeCompanyId) {
    activeCompanyId = null;
  }
};

export const getActiveCompanyContext = () => activeCompanyId;

const trimTrailingSlashes = (value) => String(value || "").replace(/\/+$/, "");

/** ¿El request apunta a la API de Winscore (URL absoluta o relativa con baseURL de la API)? */
export const targetsApiBase = (config, apiBaseUrl) => {
  const base = trimTrailingSlashes(apiBaseUrl);
  if (!base) return false;

  const url = String(config?.url || "");
  if (ABSOLUTE_URL.test(url) || url.startsWith("//")) {
    return url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`);
  }

  const requestBase = trimTrailingSlashes(config?.baseURL);
  return Boolean(requestBase) && (requestBase === base || requestBase.startsWith(`${base}/`));
};

const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    const value = headers.get(name);
    if (value !== undefined && value !== null) return value;
  }
  const key = Object.keys(headers).find((header) => header.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : undefined;
};

/**
 * Añade X-Hayllis-Company cuando hay compañía válida y el destino es la API. Una cabecera
 * explícita ya presente se respeta. Compatible con AxiosHeaders (set/get) y objetos planos.
 */
export const attachCompanyContextHeader = (config, { apiBaseUrl, companyId }) => {
  const id = parseCompanyId(companyId);
  if (!config || !id || !targetsApiBase(config, apiBaseUrl)) return config;

  const headers = config.headers || {};
  const existing = readHeader(headers, COMPANY_CONTEXT_HEADER);
  if (existing === undefined || existing === null || existing === "") {
    if (typeof headers.set === "function") headers.set(COMPANY_CONTEXT_HEADER, String(id));
    else headers[COMPANY_CONTEXT_HEADER] = String(id);
  }
  config.headers = headers;
  return config;
};

/** Interceptor de request para cualquier instancia axios; lee el contexto en cada request. */
export const createCompanyContextInterceptor = (apiBaseUrl, readCompanyId = getActiveCompanyContext) =>
  (config) => attachCompanyContextHeader(config, { apiBaseUrl, companyId: readCompanyId() });
